import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/landing/ui";
import { PhoneDemo } from "@/components/landing/ProblemBlock";

const EASE = [0.16, 1, 0.3, 1];

function PreviewNav() {
  return (
    <header className="flex h-16 items-center justify-between border-b border-black/10 bg-white px-6 sm:px-10">
      <span className="font-dmsans text-xl font-bold tracking-[0.08em] text-black">MOM</span>
      <nav className="hidden items-center gap-7 lg:flex">
        {["Para profissionais", "Funcionalidades", "Como funciona", "Histórias", "Planos"].map((l) => (
          <span key={l} className="font-dmsans text-[12px] font-medium uppercase tracking-[0.12em] text-black/60">
            {l}
          </span>
        ))}
      </nav>
      <div className="flex items-center gap-6">
        <span className="hidden font-dmsans text-[12px] font-medium uppercase tracking-[0.12em] text-black/60 sm:block">Entrar</span>
        <span className="rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim px-5 py-2.5 font-dmsans text-[12px] font-semibold text-white">
          Começar agora
        </span>
      </div>
    </header>
  );
}

function HeroA() {
  return (
    <section data-testid="mom-hero" className="grid grid-cols-1 lg:grid-cols-2">
      <div className="relative flex flex-col justify-center overflow-hidden bg-[radial-gradient(circle_at_35%_30%,#C11B3E,#A91532_55%,#8A0F27)] px-6 py-20 sm:px-12 lg:py-28">
        <MaskedLines
          as="h1"
          delay={0.15}
          stagger={0.13}
          className="font-serif text-5xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl"
          lines={["Não sabe", "o que fazer", <em key="a" className="italic">agora?</em>]}
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.75 }} className="mt-7 font-serif text-2xl font-medium text-white sm:text-3xl">
          Pergunta pro MOM.
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.9 }} className="mt-3 font-dmsans text-sm text-white/75">
          Uma situação. Uma missão.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 1.05 }} className="mt-9">
          <button data-testid="mom-hero-cta" className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 font-dmsans text-sm font-semibold uppercase tracking-[0.08em] text-black transition-colors duration-300 hover:bg-white/90">
            Começar agora
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.25 }} className="mt-10 font-dmsans text-[10px] font-medium uppercase tracking-[0.28em] text-white/60">
          Seu assistente inteligente<br />para a vida real do salão
        </motion.p>
      </div>
      <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: EASE }} className="relative min-h-[420px] overflow-hidden bg-[#E8E0D5] lg:min-h-[620px]">
        <img src="/mom/hero-a.jpg" alt="Profissional do salão usando o MOM no celular" className="absolute inset-0 h-full w-full object-cover object-top" />
        <motion.p initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.1 }} className="absolute right-6 top-8 max-w-[190px] text-right font-script text-3xl font-semibold leading-tight text-mom-carmim sm:right-10">
          Mais tempo para o que você ama
        </motion.p>
      </motion.div>
    </section>
  );
}

function MecanismoD() {
  return (
    <section data-testid="mom-mecanismo" className="bg-black text-white">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="relative min-h-[320px] overflow-hidden lg:col-span-4 lg:min-h-[600px]">
          <img loading="lazy" src="/mom/mecanismo-d.jpg" alt="Cliente relaxando no lavatório" className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="flex flex-col justify-center px-6 py-16 sm:px-12 lg:col-span-5 lg:py-24">
          <MaskedLines
            inView
            as="h2"
            className="font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl"
            lines={["O problema", "entra.", <em key="m" className="italic text-mom-carmim">Uma missão sai.</em>]}
          />
          <Reveal delay={0.2}>
            <div className="mt-8 max-w-md space-y-2 font-dmsans text-sm leading-relaxed text-white/65 sm:text-base">
              <p>Você conta o que está acontecendo.</p>
              <p>O MOM entende o contexto.</p>
              <p className="text-white">Você recebe uma missão para executar agora.</p>
            </div>
            <span className="mt-8 block h-px w-10 bg-mom-carmim" />
            <p className="mt-6 font-dmsans text-[10px] font-medium uppercase tracking-[0.28em] text-white/50">
              Inteligência real<br />para profissionais reais.
            </p>
          </Reveal>
        </div>
        <div className="flex items-center justify-center px-6 pb-16 lg:col-span-3 lg:pb-0">
          <Reveal delay={0.3}>
            <PhoneDemo />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ComercialB() {
  return (
    <section data-testid="mom-comercial" className="bg-mom-cream text-black">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 sm:px-10 lg:grid-cols-2 lg:py-28">
        <div>
          <Reveal>
            <p className="font-dmsans text-[10px] font-semibold uppercase tracking-[0.28em] text-black/50">
              Mais clientes. Mais resultados.
            </p>
          </Reveal>
          <MaskedLines
            inView
            as="h2"
            className="mt-6 font-serif text-4xl font-medium leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl"
            lines={["Cliente Traz Cliente.", <em key="c" className="italic text-mom-carmim">Caixa Hoje.</em>]}
          />
          <Reveal delay={0.15}>
            <p className="mt-7 max-w-md font-dmsans text-sm leading-relaxed text-black/60 sm:text-base">
              O MOM transforma clientes, produtos, horários e oportunidades
              esquecidas em uma missão comercial prática para executar agora.
            </p>
            <button data-testid="mom-comercial-cta" className="group mt-9 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-mom-vinho to-mom-carmim px-7 py-3.5 font-dmsans text-sm font-semibold uppercase tracking-[0.08em] text-white transition-[filter] hover:brightness-110">
              Começar agora
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="relative">
          <div className="overflow-hidden rounded-3xl">
            <img loading="lazy" src="/mom/comercial-b.jpg" alt="Profissional agindo no celular dentro do salão" className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <p className="absolute -left-4 bottom-10 hidden font-script text-3xl font-semibold text-mom-carmim [writing-mode:vertical-rl] lg:block">
            um amanhã mais seu
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ContextoC() {
  return (
    <section data-testid="mom-contexto" className="relative min-h-[70vh] overflow-hidden bg-black text-white">
      <img loading="lazy" src="/mom/contexto-c.jpg" alt="Salão em funcionamento, equipe atendendo clientes" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
      <div className="relative mx-auto flex min-h-[70vh] w-full max-w-6xl flex-col justify-end px-6 pb-16 pt-24 sm:px-10">
        <Reveal>
          <p className="font-dmsans text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60">
            Beleza em movimento
          </p>
        </Reveal>
        <MaskedLines
          inView
          as="h2"
          className="mt-5 font-serif text-5xl font-medium leading-[1.04] tracking-tight sm:text-6xl"
          lines={["O salão", <em key="s" className="italic">não para.</em>]}
        />
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-lg font-dmsans text-sm leading-relaxed text-white/75 sm:text-base">
            Enquanto clientes chegam, mensagens aparecem e horários mudam, o MOM
            ajuda você a escolher o próximo movimento.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function MomPreview() {
  return (
    <div className="min-h-screen bg-white font-dmsans text-black antialiased">
      <PreviewNav />
      <HeroA />
      <MecanismoD />
      <ComercialB />
      <ContextoC />
      <div className="bg-black px-6 py-8 text-center">
        <p className="font-dmsans text-[11px] uppercase tracking-[0.24em] text-white/40">
          Prévia visual MOM — 4 áreas com as imagens oficiais, aguardando aprovação
        </p>
      </div>
    </div>
  );
}
